import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Grid } from '@mui/material';
import { Dropdown } from '@/components/shared/Dropdown';
import { InputText } from '@/components/shared/InputText';
import { useIsInstaAdmin } from '@/contexts/user/UserContext';
import { Title } from '@/typography/Typography';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useZenegyPortfolioTenants } from '@/services/paymentsQuery';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';
import {
    PrimaryButtonStyle,
    SecondaryButtonStyle
} from '@/pages/company/common/IntegrationDialog';

interface ZenegyPortfolioConnectProps {
    onClose?: () => void;
    onSaveSuccess: any;
}

export const ZenegyPortfolioConnect = (props: ZenegyPortfolioConnectProps) => {
    const { t } = useTranslation();
    const [tenantIdentifier, setTenantIdentifier] = useState<string>('');

    // The tenant list is InstaPaid's whole Portfolio (every customer's name and
    // VAT number), so only InstaPaid staff get the dropdown; a company admin types
    // the VAT number of their own Zenegy company instead. The list endpoint refuses
    // non-staff, so the query must not even be issued for them.
    const isInstaAdmin = useIsInstaAdmin();
    const {
        data: tenantsResponse,
        isPending,
        error: tenantsError
    } = useZenegyPortfolioTenants(isInstaAdmin);
    const { setZenegyPortfolioTenant } = usePaymentsMutations();

    // The VAT number is what the admin knows and what the bind endpoint keys on;
    // the company name only rides along so a wrong pick is obvious before they
    // hit Connect. Both are searchable — a lookup by name is just as natural.
    const choices = useMemo(
        () =>
            (tenantsResponse?.data?.tenants ?? []).map((tenant) => ({
                label: `${tenant.identificationNumber} - ${tenant.name}`,
                value: tenant.identificationNumber,
                searchValue: `${tenant.identificationNumber} ${tenant.name}`
            })),
        [tenantsResponse]
    );

    // The tenant list is fetched live from Zenegy on the shared Portfolio token,
    // so a failure here is expected often enough to surface: without it the admin
    // just gets an empty dropdown with nothing to explain it.
    useHandleResponse({
        isSuccess: setZenegyPortfolioTenant.isSuccess,
        error: setZenegyPortfolioTenant.error ?? tenantsError ?? null,
        isPending: setZenegyPortfolioTenant.isPending,
        successAlertMessage: t('connected'),
        isSuccessCallBack: () => {
            props.onSaveSuccess();
        }
    });

    return (
        <>
            <Box width={'100%'} margin={'20px 0 10px'}>
                <Title mb={0.5}>{t('zenegy_portfolio_vat_number')}</Title>
                {isInstaAdmin ? (
                    <Dropdown
                        label={t('zenegy_portfolio_vat_number_placeholder')}
                        choices={choices}
                        value={tenantIdentifier}
                        onChange={(value) => setTenantIdentifier(value ?? '')}
                        isDisabled={isPending}
                        searchable
                        allowClear={false}
                    />
                ) : (
                    <InputText
                        value={tenantIdentifier}
                        placeholder={t(
                            'zenegy_portfolio_vat_number_input_placeholder'
                        )}
                        onChange={(e) =>
                            setTenantIdentifier(e.target.value.trim())
                        }
                    />
                )}
            </Box>

            <Grid
                container
                spacing={2}
                justifyContent={'space-around'}
                sx={{ m: '20px 0' }}
            >
                <Grid>
                    <SecondaryButtonStyle
                        onClick={props.onClose}
                        sx={{ width: '170px' }}
                    >
                        {t('back')}
                    </SecondaryButtonStyle>
                </Grid>

                <Grid>
                    <PrimaryButtonStyle
                        onClick={() =>
                            setZenegyPortfolioTenant.mutate(tenantIdentifier)
                        }
                        sx={{ width: '170px' }}
                        loading={setZenegyPortfolioTenant.isPending}
                        disabled={!tenantIdentifier}
                    >
                        {t('connect')}
                    </PrimaryButtonStyle>
                </Grid>
            </Grid>
        </>
    );
};
